import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import GridContainer from "@insite/mobius/GridContainer";
import Link from "@insite/mobius/Link/Link";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Pagination from "@insite/mobius/Pagination";
import Typography from "@insite/mobius/Typography";
import StoreWholegoodProductContext from "@store/Components/Store/StoreSchematicsView/StoreWholegoodProductContext";
import styles from "@store/Components/Store/StoreSchematicsView/styles";
import GridItem from "@store/MobiusOverrides/GridItem/GridItem";
import { getShowNormalPrice, getShowRetailPrice } from "@store/Store/Context/ContextSelectors";
import { getWholegoodsViewModel } from "@store/Store/Data/Parts/PartsSelectors";
import addParameterWholegoods from "@store/Store/Pages/ProductDetails/Wholegoods/Handlers/AddParameterWholegoods";
import { getProductDetailsWholegoodsDataView } from "@store/Store/Pages/ProductDetails/Wholegoods/ProductDetailsWholegoodsSelectors";
import StoreApplicationState from "@store/Store/StoreApplicationState";
import React from "react";
import { connect, ResolveThunks } from "react-redux";

// Poolcorp Custom Code - Start
import StorePartsActions from "@store/Components/Store/StoreSchematicsView/StorePartsActions";
// Poolcorp Custom Code - End

type Props = HasProductContext & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const mapStateToProps = (state: StoreApplicationState) => {
    const stateContext = state.store.pages.productDetailsWholegoods;
    const isReady = stateContext.isReady;

    let wholegoodsDataView;
    let wholegoods;

    if (isReady) {
        wholegoodsDataView = getProductDetailsWholegoodsDataView(state);
        wholegoods = getWholegoodsViewModel(state, wholegoodsDataView);
    }

    return {
        isReady,
        wholegoods,
        isLoading: stateContext.isLoading,
        pagination: wholegoodsDataView?.pagination,
        isSignedIn: state.context.session.userName && !getCurrentUserIsGuest(state),
        rememberMe: state.context.session.rememberMe,
        showNormalPrice: getShowNormalPrice(state),
        showRetailPrice: getShowRetailPrice(state),
    };
};

const mapDispatchToProps = {
    addParameterWholegoods,
};

const StoreWholegoodsView: React.FC<Props> = ({
    productContext: { product },
    addParameterWholegoods,
    isReady,
    isLoading,
    isSignedIn,
    rememberMe,
    wholegoods,
    pagination,
    showNormalPrice,
    showRetailPrice,
}) => {
    const changePage = (page: number) => {
        addParameterWholegoods({ page });
    };

    const changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        addParameterWholegoods({ pageSize: parseInt(event.currentTarget.value, 10) });
    };

    if (!isReady) {
        return (
            <StyledWrapper {...styles.loadingSpinner}>
                <LoadingSpinner />
            </StyledWrapper>
        );
    }

    if (!wholegoods?.length) {
        return (
            <Typography {...styles.noResults}>{translate("There are no wholegoods available.")}</Typography>
        );
    }

    return (
        <>
            {wholegoods && wholegoods.length > 0 && (
                <>
                    <LoadingOverlay loading={isLoading} {...styles.loadingOverlay}>
                        <StyledWrapper {...styles.container}>
                            <GridContainer {...styles.tableGridHead}>
                                <GridItem {...styles.productNumberHeaderGridCell}>
                                    <Typography {...styles.tableGridHeaderText}>
                                        {translate("Product #")}
                                    </Typography>
                                </GridItem>
                                <GridItem {...styles.wholegoodsDescriptionHeaderGridCell}>
                                    <Typography {...styles.tableGridHeaderText}>
                                        {translate("Description")}
                                    </Typography>
                                </GridItem>
                                <GridItem {...styles.availabilityHeaderGridCell}>
                                    {(isSignedIn || rememberMe) && (
                                        <Typography {...styles.tableGridHeaderText}>
                                            {translate("Availability")}
                                        </Typography>
                                    )}
                                </GridItem>
                                <GridItem {...styles.normalPriceHeaderGridCell}>
                                    {((isSignedIn || rememberMe) && showNormalPrice) && (
                                        <Typography {...styles.tableGridHeaderText}>
                                            {translate("Price")}
                                        </Typography>
                                    )}
                                </GridItem>
                                <GridItem {...styles.retailPriceHeaderGridCell}>
                                    {((isSignedIn || rememberMe) && showRetailPrice) && (
                                        <Typography {...styles.tableGridHeaderText}>
                                            {translate("Retail Price")}
                                        </Typography>
                                    )}
                                </GridItem>
                            </GridContainer>
                            <GridContainer {...styles.tableGridBody}>
                                {wholegoods.map((wholegood: ProductModel) => (
                                    <StoreWholegoodProductContext product={wholegood} key={wholegood.id}>
                                        <GridItem {...styles.tableGridRow}>
                                            <GridContainer {...styles.tableGridRowContainer}>
                                                <GridItem {...styles.productNumberBodyGridCell}>
                                                    <Typography>
                                                        {wholegood.productNumber}
                                                    </Typography>
                                                </GridItem>
                                                <GridItem {...styles.wholegoodsDescriptionBodyGridCell}>
                                                    <Link href={wholegood.canonicalUrl}>
                                                        {wholegood.productTitle}
                                                    </Link>
                                                </GridItem>
                                                <GridItem {...styles.mobileProductNumberBodyGridCell}>
                                                    <Typography {...styles.mobileHeader}>
                                                        {`${translate("Product #")}: `}
                                                    </Typography>
                                                    <Typography>
                                                        {wholegood.productNumber}
                                                    </Typography>
                                                </GridItem>
                                                <StorePartsActions />
                                            </GridContainer>
                                        </GridItem>
                                    </StoreWholegoodProductContext>
                                ))}
                            </GridContainer>
                        </StyledWrapper>
                        {isSignedIn && wholegoods.some(x => !x) && (
                            <Typography {...styles.globalNote}>
                                {translate("*Please contact local branch to inquire about this item.")}
                            </Typography>
                        )}
                    </LoadingOverlay>
                    {pagination && (
                        <Pagination
                            {...styles.pagination}
                            resultsCount={pagination.totalItemCount}
                            currentPage={pagination.page}
                            resultsPerPage={pagination.pageSize}
                            resultsPerPageOptions={pagination.pageSizeOptions}
                            onChangePage={changePage}
                            onChangeResultsPerPage={changeResultsPerPage}
                        />
                    )} 
                </>
            )}
        </>
    );
};

export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(StoreWholegoodsView));
